import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import api from '../services/api';

const CourseForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        short_description: '',
        difficulty: 'beginner',
        estimated_hours: '',
        is_published: false,
        tags: [],
    });
    const [tagInput, setTagInput] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [userRole, setUserRole] = useState(null);
    useEffect(() => {
        if (id) {
            setIsEdit(true);
            fetchCourse();
            fetchUser();
        }
    }, [id]);

    const fetchCourse = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/courses/${id}/`);
            const course = response.data;
            setFormData({
                title: course.title || '',
                description: course.description || '',
                short_description: course.short_description || '',
                difficulty: course.difficulty || 'beginner',
                estimated_hours: course.estimated_hours || '',
                is_published: course.is_published || false,
                tags: course.tags || [],
            });
            setTagInput(course.tags?.join(', ') || '');
        } catch (err) {
            setError('Nie udało się pobrać kursu');
        } finally {
            setLoading(false);
        }
    };
    const fetchUser = async () => {
        try {
            const response = await api.get('/users/me/');
            setUser(response.data);
        } catch (err) {
            console.error('Błąd pobierania użytkownika:', err);
        }
    };
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleTagChange = (e) => {
        setTagInput(e.target.value);
        const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
        setFormData({
            ...formData,
            tags: tags,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const data = { ...formData };
            if (!data.short_description) delete data.short_description;
            if (!data.estimated_hours) delete data.estimated_hours;

            if (isEdit) {
                await api.patch(`/courses/${id}/`, data);
                setSuccess('Kurs zaktualizowany pomyślnie!');
            } else {
                const response = await api.post('/courses/', data);
                setSuccess('Kurs utworzony pomyślnie!');
                setTimeout(() => navigate(`/courses/${response.data.id}`), 1500);
            }
        } catch (err) {
            if (err.response?.data) {
                const errors = err.response.data;
                const firstError = Object.values(errors)[0];
                setError(Array.isArray(firstError) ? firstError[0] : 'Błąd zapisu kursu');
            } else {
                setError('Błąd połączenia z serwerem');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Ładowanie kursu...</p>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <Row>
                <Col md={8} className="mx-auto">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <h2 className="mb-4">
                                {isEdit ? '✏️ Edytuj kurs' : '📝 Utwórz nowy kurs'}
                            </h2>

                            {error && <Alert variant="danger">{error}</Alert>}
                            {success && <Alert variant="success">{success}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tytuł kursu *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder="Wprowadź tytuł kursu"
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Krótki opis</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="short_description"
                                        value={formData.short_description}
                                        onChange={handleChange}
                                        placeholder="Krótki opis (maks. 300 znaków)"
                                        maxLength={300}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Opis kursu *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Szczegółowy opis kursu..."
                                        required
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Poziom trudności</Form.Label>
                                            <Form.Select
                                                name="difficulty"
                                                value={formData.difficulty}
                                                onChange={handleChange}
                                            >
                                                <option value="beginner">Początkujący</option>
                                                <option value="intermediate">Średniozaawansowany</option>
                                                <option value="advanced">Zaawansowany</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Szacowany czas (godziny)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="estimated_hours"
                                                value={formData.estimated_hours}
                                                onChange={handleChange}
                                                placeholder="np. 20"
                                                min="1"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Tagi (oddziel przecinkami)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={tagInput}
                                        onChange={handleTagChange}
                                        placeholder="np. python, django, react"
                                    />
                                    <Form.Text className="text-muted">
                                        Wprowadź tagi oddzielone przecinkami
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="is_published"
                                        label="Opublikuj kurs (widoczny dla studentów)"
                                        checked={formData.is_published}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                
                                <div className="d-flex gap-2">
                                    <Button variant="primary" type="submit" disabled={saving} onClick={() => navigate(-1)}>
                                        {saving ? 'Zapisywanie...' : isEdit ? '💾 Zapisz zmiany' : '➕ Utwórz kurs'}
                                    </Button>
                                    <Button variant="secondary" onClick={() => navigate(-1)}>
                                        Anuluj
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default CourseForm;